import Icon from 'common/Icon'
import { RED } from 'constants/colors'
import { useState, useEffect, memo, useRef } from 'react'
import Alert from 'react-bootstrap/Alert'

type ErrorComponentProps = {
  error: AlertMessage
  disappearInterval?: number
  className?: string
  disappearCallback?: () => void
  dismissible?: boolean
  showIcon?: boolean
}

// Success component and Error component looks similar
// but keeping them seperately for future changes
const ErrorComponent: React.FC<ErrorComponentProps> = ({
  error,
  className = '',
  disappearInterval = 3000,
  disappearCallback,
  dismissible = true,
  showIcon = false,
}) => {
  const showAlertTimeout = useRef(null)
  const [lastUpdated, setLastUpdated] = useState<number>(error?.timestamp ?? 0)
  const [visibleState, setVisibleState] = useState<boolean>(!!error?.timestamp)
  useEffect(() => {
    if (error?.timestamp && lastUpdated !== error?.timestamp) {
      setVisibleState(true)
      setLastUpdated(error.timestamp)

      if (disappearInterval > 0) {
        if (showAlertTimeout?.current) {
          clearTimeout(showAlertTimeout.current)
        }

        showAlertTimeout.current = setTimeout(() => {
          setVisibleState(false)
          /* The disapper callback is taken to set the error to null as in case of modals, the cross icon in modal
          is pushed down if there's an error. setVisible to false doesn't set the error in parent component to null,
          so the cross icon is still pushed down.
          TODO Add logic that in case of setVisible to false, the error in parent component is also set to null.*/
          !!disappearCallback && disappearCallback()
          showAlertTimeout.current = null
        }, disappearInterval)
      }
    } else {
      setVisibleState(false)
    }

    return () => {
      if (showAlertTimeout?.current) {
        clearTimeout(showAlertTimeout.current)
      }
    }
  }, [error?.timestamp])

  if (visibleState) {
    return (
      <Alert
        variant="danger"
        className={className}
        onClose={() => {
          setVisibleState(false)
          !!disappearCallback && disappearCallback()
        }}
        dismissible={dismissible}
      >
        <div className="d-flex">
          {showIcon && <Icon icon="alert" fill={RED} className="fa-lg mr-3" />}
          <span> {error?.message ?? 'Oops, something went wrong!'}</span>
        </div>
      </Alert>
    )
  }

  return null
}

const successPropsComparison = (
  previousProps: ErrorComponentProps,
  nextProps: ErrorComponentProps
) => {
  return previousProps?.error?.timestamp === nextProps?.error?.timestamp
}

export default memo(ErrorComponent, successPropsComparison)
